import map from "lodash/map"
import get from "lodash/get"
import filter from "lodash/filter"
import isEmpty from "lodash/isEmpty"
import { Component } from "react"
import PropTypes from "prop-types"
import { styles } from "constants-lib/styles"
import { getWindow } from "utils-lib/getWindow"
import { isWMPath } from "utils-lib/isWMPath"
import { LINE_OF_BUSINESS, MASTER_BILL_TYPE } from "constants-lib/common"
import { withAuthentication } from "hoc-lib/appAuthentication"
import FooterContent from "./components/FooterContent/FooterContent"
import FooterLegal from "./components/FooterLegal/FooterLegal"
import BrightEdge from "./components/FooterContent/BrightEdge"
import MobileFooter from "./components/MobileFooter/MobileFooter"
import "./Footer.scss"

const ssrWindow = getWindow()

class Footer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasMasterBillType: false,
    }
  }

  componentDidMount() {
    this.checkForMasterBillAccount()
  }

  componentDidUpdate(prevProps, prevState) {
    const { userManageAccount } = this.props
    if (
      prevProps.userManageAccount?.userAccountStatus !==
        userManageAccount.userAccountStatus &&
      userManageAccount.userAccountStatus === `fulfilled`
    ) {
      this.checkForMasterBillAccount()
    }

    if (prevState.hasMasterBillType !== this.state.hasMasterBillType) {
      this.checkForMasterBillAccount()
    }
  }

  checkForMasterBillAccount = () => {
    const { userManageAccount } = this.props
    const checkAccounts = get(userManageAccount, `userAccountDetails`, [])
    let lob = false
    let countMasterBillAccounts = 0
    let countNonMasterBillAccounts = 0

    map(checkAccounts, (accounts) => {
      lob = accounts.lineOfBusiness === LINE_OF_BUSINESS.RESIDENTIAL
      if (lob && accounts.billType === MASTER_BILL_TYPE) {
        countMasterBillAccounts += 1
      } else if (lob && accounts.billType !== MASTER_BILL_TYPE) {
        countNonMasterBillAccounts += 1
      }
    })
    // Define Mixed Account - RESI both MASTR BILL ALL + NON MASTR BILL ALL (not only a MultiAccount)
    const isMixedMasterBillAccount =
      countNonMasterBillAccounts >= 1 && countMasterBillAccounts >= 1
    const isMasterBillAccountOnly =
      countNonMasterBillAccounts === 0 && countMasterBillAccounts >= 1

    if (isMasterBillAccountOnly) {
      this.setState({ hasMasterBillType: true })
    } else if (isMixedMasterBillAccount) {
      this.setState({ hasMasterBillType: false })
    }
  }

  render() {
    /* Authoring language drop-down is out of scope for Sprint 1.
     * So static json is used for languages drop-down.
     */
    const languages = [
      {
        label: `ENGLISH`,
        code: `en_US`,
        url: `/content/dam/wm/icons/us-flag-icon-small.svg`,
        altText: `USA`,
      },
      {
        label: `ENGLISH`,
        code: `en_CA`,
        url: `/content/dam/wm/icons/ca-flag-icon-small.svg`,
        altText: `Canada`,
      },
      {
        label: `FRANÇAIS`,
        code: `fr_CA`,
        url: `/content/dam/wm/icons/ca-flag-icon-small.svg`,
        altText: `Canada`,
      },
      {
        label: `SPANISH`,
        code: `es_US`,
        url: `/content/dam/wm/icons/us-flag-icon-small.svg`,
        altText: `USA`,
      },
    ]
    const { pageLocales, isSingleAccount, isLoggedIn, brightEdge } = this.props
    const pageLanguages = filter(
      languages,
      (language) =>
        !isEmpty(pageLocales) && pageLocales.indexOf(language.code) !== -1,
    )
    if (isLoggedIn && isSingleAccount && isWMPath()) {
      return (
        <footer className="redesigned-footer mt-auto" id="redesigned-footer">
          <FooterLegal
            copyright={this.props.copyrightMessaging}
            languages={
              !isEmpty(pageLocales) && pageLocales.length > 0
                ? pageLanguages
                : languages
            }
            legalLinks={this.props.legalLinks}
            siteLanguage={this.props.siteLanguage}
            socialMediaLinks={this.props.socialMediaLinks}
            alignment={this.props.alignment}
            footernote={this.props.footernote}
          />
        </footer>
      )
    }

    if (this.props.logoIcon) {
      return (
        <div
          data-testid="Footer"
          id="footerContent"
          className={`Footer ${
            ssrWindow.location.pathname.length <= 7 ? "home-mobile-footer" : ""
          }`}
          tabIndex="-1"
        >
          <footer
            className="container maax-font-weight-regular"
            style={{
              color: styles.footer.quickLinks.color,
              paddingTop: 32,
              paddingBottom: 32,
            }}
          >
            <div className="desktopFooter">
              <BrightEdge brightEdgeServletData={brightEdge} />
              <FooterContent
                hasMasterBillType={this.state.hasMasterBillType}
                columns={this.props.columns}
                logo={this.props.logoIcon}
                logoAltText={this.props.logoAltText}
                logolink={this.props.logolink}
                quickLinks={this.props.quickLinks}
                socialMediaLinks={this.props.socialMediaLinks}
                text={this.props.footerText}
                languages={
                  !isEmpty(pageLocales) && pageLocales.length > 0
                    ? pageLanguages
                    : languages
                }
              />
            </div>
            <div className="mobileFooter">
              <MobileFooter
                columns={this.props.columns}
                logo={this.props.logoIcon}
                logoAltText={this.props.logoAltText}
                logolink={this.props.logolink}
                quickLinks={this.props.quickLinks}
                socialMediaLinks={this.props.socialMediaLinks}
                text={this.props.footerText}
                languages={
                  !isEmpty(pageLocales) && pageLocales.length > 0
                    ? pageLanguages
                    : languages
                }
              />
            </div>
          </footer>
          <FooterLegal
            copyright={this.props.copyrightMessaging}
            languages={
              !isEmpty(pageLocales) && pageLocales.length > 0
                ? pageLanguages
                : languages
            }
            legalLinks={this.props.legalLinks}
            siteLanguage={this.props.siteLanguage}
            socialMediaLinks={this.props.socialMediaLinks}
            alignment={this.props.alignment}
            footernote={this.props.footernote}
          />
        </div>
      )
    }
    return null
  }
}

Footer.propTypes = {
  // AEM_DEV_NOTE: Passed in from AEM (see mock.model.json "footer" item for
  //  JSON structure/naming conventions and expectations)
  columns: PropTypes.array.isRequired,
  copyrightMessaging: PropTypes.string.isRequired,
  logoIcon: PropTypes.string.isRequired,
  logoAltText: PropTypes.string,
  footerText: PropTypes.string.isRequired,
  legalLinks: PropTypes.array.isRequired,
  logolink: PropTypes.string.isRequired,
  quickLinks: PropTypes.array,
  siteLanguage: PropTypes.object.isRequired,
  socialMediaLinks: PropTypes.array.isRequired,
  pageLocales: PropTypes.array,
  alignment: PropTypes.string,
  footernote: PropTypes.string,
  userManageAccount: PropTypes.object.isRequired,
  isSingleAccount: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  brightEdge: PropTypes.string,
}

Footer.defaultProps = {
  quickLinks: [],
  pageLocales: [],
  logoAltText: "",
  alignment: ``,
  footernote: ``,
  brightEdge: "",
}

export default withAuthentication(Footer)
